"use client";

import { debounce } from "lodash";
import { Search, X } from "lucide-react";
import Image from "next/image";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Asset, TransactionsTypes } from "@/app/dashboard/transactions/types";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatNumber } from "@/lib/utils";
import { getAssets } from "@/services/controllers/assets";
export default function SearchCoin({
  onSelectCoin,
  className,
  defaultItem,
}: {
  onSelectCoin: React.Dispatch<Asset | TransactionsTypes | any | undefined>;
  className?: string;
  defaultItem?: any;
}) {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [suggestions, setSuggestions] = useState<Asset[]>([]);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCoin, setSelectedCoin] = useState<Asset | undefined>(
    defaultItem,
  );

  const resetCoin = useCallback(() => {
    setSelectedCoin(undefined);
    const input: any = document.getElementById("orders-search-input");
    input.value = "";
    setSearchTerm("");
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("reset-orders", resetCoin);
    }

    return () => {
      window.removeEventListener("reset-orders", resetCoin);
    };
  }, [resetCoin]);

  useEffect(() => {
    const controller = new AbortController();
    if (searchTerm.length >= 3) {
      setLoading(true);
      setSuggestions([]);
      getAssets({
        signal: controller.signal,
        params: {
          search: searchTerm,
          limit: 30,
          offset: 0,
        },
      })
        .then((response) => {
          setSuggestions(response.data.data);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    return () => {
      controller.abort();
    };
  }, [searchTerm]);

  useEffect(() => {
    if (defaultItem?.asset_name) {
      const input: any = document.getElementById("orders-search-input");
      input.value = defaultItem.asset_name.toUpperCase();
    }
  }, [defaultItem?.asset_name]);

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSelectedCoin(undefined);
    const term = e.target.value;
    setSearchTerm(term);
    if (term.length >= 2) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, []);
  const handleSuggestionClick = useCallback(
    (suggestion: Asset) => {
      setSelectedCoin(suggestion);
      const input: any = document.getElementById("orders-search-input");
      input.value = suggestion?.asset_data?.name;
      setShowDropdown(false);
      onSelectCoin(suggestion);
    },
    [onSelectCoin],
  );

  const debouncedSearch = useMemo(() => {
    return debounce(handleSearch, 250);
  }, [handleSearch]);

  const onReset = useCallback(() => {
    const input: any = document.getElementById("orders-search-input");
    input.value = "";
    setSearchTerm("");
    setSuggestions([]);
    setSelectedCoin(undefined);
    setShowDropdown(false);
    onSelectCoin(undefined);
  }, [onSelectCoin]);

  return (
    <div className={cn("relative w-full", className)}>
      <div className="relative felx items-center">
        {searchTerm !== "" ? (
          <X
            className="w-4 h-auto absolute right-0 me-10 top-[30%] cursor-pointer"
            onClick={onReset}
          />
        ) : (
          <></>
        )}
        {selectedCoin?.id != null ? (
          <Image
            src={selectedCoin?.asset_data?.image ?? "/placeholder.svg"}
            width={30}
            height={30}
            alt="placeholder"
            className="absolute top-1.5 left-1.5 rounded-full object-contain w-6 h-6 aspect-square"
          />
        ) : (
          <></>
        )}
        <Input
          id="orders-search-input"
          placeholder="Search..."
          onChange={debouncedSearch}
          className={twMerge(
            selectedCoin?.id != null ? "ps-10" : "",
            "w-full pr-10",
          )}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
      </div>
      {showDropdown && (
        <div className="absolute z-10 w-full rounded-sm shadow-sm mt-2 bg-sidebar border max-h-[300px] ">
          <ScrollArea className="h-[300px] p-1 pr-3">
            {loading
              ? [...Array(10)].map(() => (
                  <div
                    className="flex gap-2 items-center px-2 py-1 my-1 w-full  rounded-sm"
                    key={Math.random() * 10}
                  >
                    <Skeleton className="min-w-6 min-h-6 my-2 rounded-full" />
                    <Skeleton className="w-full h-5" />
                  </div>
                ))
              : suggestions.map((suggestion) => (
                  <div
                    key={suggestion.id}
                    className="cursor-pointer hover:bg-muted flex gap-2 items-center px-2 py-1 my-1  rounded-sm"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <Image
                      src={
                        suggestion?.asset_data?.image &&
                        suggestion.asset_data.image.startsWith("https:")
                          ? suggestion.asset_data.image
                          : "/placeholder.svg"
                      }
                      alt={suggestion.asset_name ?? ""}
                      width={24}
                      height={24}
                      className="rounded-full object-contain w-6 h-6 aspect-square "
                    />
                    <p className="text-left font-medium text-xs flex-1">
                      {suggestion?.asset_data?.name}
                    </p>
                    <p className="text-right text-xs font-medium text-sm">
                      {formatNumber(
                        suggestion?.asset_data?.current_price ?? 0,
                        true,
                      )}
                    </p>
                  </div>
                ))}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
